import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import {
    AddressModel,
    ArrayModel,
    ConfigService,
    ModalClass,
    TimesheetAddressModel,
    TimesheetAndExpensesService,
    TimesheetEntryInterface,
    TimesheetEntryModel,
    TimesheetManualEntryModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import _ from 'lodash';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of, throwError } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { AddressSelectInfoComponent } from './address-select-info/address-select-info.component';

@Component({
    selector: 'pace-address-select',
    templateUrl: './address-select.component.html',
    styleUrls: ['./address-select.component.scss'],
})
export class AddressSelectComponent implements OnInit {
    @Input()
    public preselectedAddress: TimesheetAddressModel | undefined;

    @Input()
    public timesheetEntries: ArrayModel<TimesheetEntryInterface> | undefined;

    @Output()
    public timeSheetAddressAdded = new EventEmitter<TimesheetAddressModel>();

    // internal variables
    public addresses: AddressModel[];
    public selectedAddress: TimesheetAddressModel;
    public selectedAddressIndex = -1;
    public expandedIndex: number | undefined;

    public isEditable = true;
    public orderBy = '';
    public reverse = false;
    public readonly enableHideTimesheetAddress: boolean;
    public isResetDeletedAddressesBtnDisabled: boolean;
    public isNewAddressEntry = false;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        public readonly modalInstance: BsModalRef,
        public readonly modalService: BsModalService,
        private readonly configService: ConfigService,
        private readonly timesheetAndExpensesService: TimesheetAndExpensesService,
    ) {
        this.enableHideTimesheetAddress = this.configService.getEnableHideTimesheetAddress();
        this.addresses = this.timesheetAndExpensesService.getAddresses().slice();
    }

    public ngOnInit(): void {
        if (typeof this.preselectedAddress !== 'undefined') {
            const selectedAddressInput = this.preselectedAddress; // assignment to const to avoid undefined warning in _.find
            const selectedAddressIndex = this.addresses.findIndex((address) =>
                selectedAddressInput.equalsTo(address.getTimesheetAddress()),
            );
            if (selectedAddressIndex > -1) {
                this.loadAddress(selectedAddressIndex);
            }
        }

        if (this.enableHideTimesheetAddress) {
            this.addresses = this.getVisibleAddresses();
            this.setAllowToHidePropertyForAddresses();
        }
    }

    public setAllowToHidePropertyForAddresses(): void {
        if (typeof this.timesheetEntries === 'undefined') {
            return;
        }
        let selectedAddresses: TimesheetAddressModel[] = [];
        // Initially, allow address to hide for all the customer address which we get from NDP
        this.addresses.map((address) => {
            address.allowToHide = address.isFromAddressSet;
        });

        // Logic: From timesheet entries, we get notification time recording entries and manual entries
        this.timesheetEntries.value.map((entry) => {
            // Get the customer addresses from Notification(i.e. NDP) and manual entries
            if (
                (entry instanceof TimesheetEntryModel || entry instanceof TimesheetManualEntryModel) &&
                typeof entry.address !== 'undefined'
            ) {
                selectedAddresses.push(entry.address);
            }
        });

        // In Notification address, we always have three entries Travel to, OSR and Trav Back which will have same addresses
        // User can select same address if multiple manual entries
        // So using uniqBy to avoid duplicate address
        selectedAddresses = _.uniqBy(selectedAddresses, 'addrNumber.value');

        // Now, we should not allow user to Hide those address as it is already selected
        this.addresses.map((address) => {
            selectedAddresses.forEach((timesheetAddress) => {
                if (
                    address.addrNumber.value.length > 0 &&
                    address.addrNumber.value === timesheetAddress.addrNumber.value
                ) {
                    address.allowToHide = false;
                }
            });
        });
    }

    public openHelp(event: { allowToHide: boolean; allowToDelete: boolean }): void {
        this.modalService.show(AddressSelectInfoComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                allowToHide: event.allowToHide,
                allowToDelete: event.allowToDelete,
            },
            ariaLabelledBy: 'address-select-info-modal-title',
        });
    }

    public setOrderBy(field: string): void {
        if (this.orderBy === field) {
            this.reverse = !this.reverse;
        } else {
            this.orderBy = field;
        }
    }

    public toggleExpand(index: number): void {
        this.expandedIndex = this.expandedIndex === index ? undefined : index;
        this.loadAddress(index);
    }

    public loadAddress(index: number): void {
        this.selectedAddressIndex = index;
        this.selectedAddress = this.addresses[index].getTimesheetAddress();

        const isExistingAddress = !!this.selectedAddress.addrNumber?.value;
        this.isEditable = !isExistingAddress;
    }

    public selectAddress(): void {
        let obs$: Observable<void>;

        if (!this.selectedAddress.addrNumber.value) {
            obs$ = this.timesheetAndExpensesService.addAddress(AddressModel.fromTimesheetAddress(this.selectedAddress));
        } else {
            obs$ = of(undefined);
        }

        obs$.pipe(
            catchError((error) => {
                this.log.error(
                    'Error in AddressSelectComponent -> selectAddress -> timesheetAndExpensesService.addAddress: ',
                    JSON.stringify(error),
                );
                return of(undefined);
            }),
        ).subscribe(() => {
            this.timeSheetAddressAdded.emit(this.selectedAddress);
            this.modalInstance.hide();
        });
    }

    public displayNewAddressForm(): void {
        this.isNewAddressEntry = true;
        this.selectedAddress = new TimesheetAddressModel();
        const address = AddressModel.fromTimesheetAddress(this.selectedAddress);
        this.addresses.push(address);

        // Automatically expand the add-new-address form
        const newIndex = this.addresses.length - 1;
        this.toggleExpand(newIndex);
    }

    public getVisibleAddresses(): AddressModel[] {
        const addresses = this.timesheetAndExpensesService.getAddresses().filter((address) => address.visible);
        this.isResetDeletedAddressesBtnDisabled = addresses.every((address) => address.visible);
        return addresses;
    }

    public resetDeletedAddresses(): void {
        this.timesheetAndExpensesService
            .resetAddresses()
            .pipe(
                take(1),
                tap(() => {
                    this.addresses = this.getVisibleAddresses();
                }),
                catchError((error) => {
                    this.log.error(
                        'AddressSelectComponent: resetDeletedAddresses: Error while resetting Addresses:',
                        JSON.stringify(error),
                    );
                    return throwError(() => error);
                }),
            )
            .subscribe();
    }

    public hideAddress(index: number): void {
        const addressToHide = this.addresses[index];
        this.timesheetAndExpensesService
            .hideAddress(addressToHide)
            .pipe(
                take(1),
                tap(() => {
                    this.addresses = this.getVisibleAddresses();
                    this.isResetDeletedAddressesBtnDisabled = false;
                    this.expandedIndex = this.expandedIndex === index ? undefined : index;
                }),
                catchError((error) => {
                    this.log.error(
                        'AddressSelectComponent:hideAddress: Error while hiding Address: ',
                        JSON.stringify(error),
                    );
                    return throwError(() => error);
                }),
            )
            .subscribe();
    }

    public deleteAddress(index: number): void {
        const addressToDelete = this.addresses[index];
        this.timesheetAndExpensesService
            .deleteAddress(addressToDelete)
            .pipe(
                take(1),
                catchError((error) => {
                    this.log.error(
                        'Error in AddressSelectComponent -> deleteAddress -> timesheetAndExpensesService.deleteAddress: ',
                        JSON.stringify(error),
                    );
                    return throwError(() => error);
                }),
            )
            .subscribe(() => {
                this.addresses.splice(index, 1);

                if (this.preselectedAddress && this.selectedAddress.equalsTo(this.preselectedAddress)) {
                    this.timeSheetAddressAdded.emit(this.selectedAddress);
                    this.selectedAddressIndex = -1;
                }
                // Reset index so that nothing remains expanded
                this.expandedIndex = undefined;
                this.isNewAddressEntry = false;
            });
    }
}
